import type {
  createWalkthroughInstanceEventBus,
  WalkthroughInstanceEventBusType,
} from '../../types/walkthroughInstanceEventBus';

const HOOK_ACK_TIMEOUT_MS = 1000;
const HOOK_DONE_TIMEOUT_MS = 10000;

type LifecycleHookEvents = {
  publish: keyof WalkthroughInstanceEventBusType;
  ack: keyof WalkthroughInstanceEventBusType;
  done: keyof WalkthroughInstanceEventBusType;
};

/**
 * Generic lifecycle hook executor — publishes an event, waits for an ack,
 * and if handling=true, waits for a done signal before resolving.
 * If no ack arrives within HOOK_ACK_TIMEOUT_MS, proceeds immediately.
 */
export function executeLifecycleHook(
  eventBus: ReturnType<typeof createWalkthroughInstanceEventBus>,
  walkthroughId: string,
  events: LifecycleHookEvents,
): Promise<void> {
  return new Promise<void>((resolve) => {
    let ackTimeout: ReturnType<typeof setTimeout>;
    let doneTimeout: ReturnType<typeof setTimeout>;
    let resolved = false;

    const finish = () => {
      if (resolved) return;
      resolved = true;
      clearTimeout(ackTimeout);
      clearTimeout(doneTimeout);
      unsubAck();
      unsubDone();
      resolve();
    };

    // subscribe to done BEFORE publishing so we never miss a synchronous done
    const unsubDone = eventBus.subscribe(events.done, () => {
      finish();
    });

    const unsubAck = eventBus.subscribe(events.ack, (data: { handling: boolean } | undefined) => {
      clearTimeout(ackTimeout);

      if (data?.handling) {
        // safety timeout so we don't hang forever
        doneTimeout = setTimeout(finish, HOOK_DONE_TIMEOUT_MS);
      } else {
        finish();
      }
    });

    // if no ack arrives in time, assume page is not handling and proceed
    ackTimeout = setTimeout(finish, HOOK_ACK_TIMEOUT_MS);

    eventBus.publish(events.publish, { walkthroughId });
  });
}

export function executePreStartHook(
  eventBus: ReturnType<typeof createWalkthroughInstanceEventBus>,
  walkthroughId: string,
) {
  return executeLifecycleHook(eventBus, walkthroughId, {
    publish: 'onWalkthroughPreStart',
    ack: 'onWalkthroughPreStartAck',
    done: 'onWalkthroughPreStartDone',
  });
}

export function executePreEndHook(
  eventBus: ReturnType<typeof createWalkthroughInstanceEventBus>,
  walkthroughId: string,
) {
  return executeLifecycleHook(eventBus, walkthroughId, {
    publish: 'onWalkthroughPreEnd',
    ack: 'onWalkthroughPreEndAck',
    done: 'onWalkthroughPreEndDone',
  });
}
